import { useEffect, useRef, useState } from 'react'
import {
  campaignProgress,
  type VictoryRecordResult
} from './campaignProgress'
import { useGameStore } from '../store/gameStore'
import { useMetaStore } from '../meta/metaStore'

/**
 * Records campaign stage victory/defeat once per terminal transition.
 * Editor playtest is ignored. Syncs reactive Ingots after victory grants.
 */
export function useCampaignProgressRecorder(): {
  lastVictoryMeta: VictoryRecordResult | null
} {
  const sessionView = useGameStore((s) => s.sessionView)
  const selectedStageId = useGameStore((s) => s.selectedStageId)
  const selectedDifficultyId = useGameStore((s) => s.selectedDifficultyId)
  const playtestMap = useGameStore((s) => s.playtestMap)
  const syncIngots = useMetaStore((s) => s.syncIngots)
  const [lastVictoryMeta, setLastVictoryMeta] = useState<VictoryRecordResult | null>(null)
  const lastTerminal = useRef<string | null>(null)

  useEffect(() => {
    const state = sessionView?.state
    if (state !== 'VICTORY' && state !== 'DEFEAT') {
      lastTerminal.current = null
      return
    }
    const stageId = sessionView?.campaign?.stageId ?? selectedStageId
    const difficultyId =
      (sessionView?.campaign?.difficultyId as typeof selectedDifficultyId | undefined) ??
      selectedDifficultyId
    const key = `${stageId}:${difficultyId}:${state}:${sessionView?.wavesCompleted}:${sessionView?.elapsedTime}`
    if (lastTerminal.current === key) return
    lastTerminal.current = key

    const isPlaytest = playtestMap !== null
    if (!stageId) {
      setLastVictoryMeta(null)
      return
    }

    if (state === 'VICTORY' && sessionView) {
      const result = campaignProgress.recordVictory({
        stageId,
        difficultyId,
        lives: sessionView.lives,
        elapsedTime: sessionView.elapsedTime,
        isPlaytest
      })
      if (result.recorded) {
        syncIngots()
        setLastVictoryMeta(result)
      } else {
        setLastVictoryMeta(null)
      }
      return
    }

    if (state === 'DEFEAT') {
      campaignProgress.recordDefeat({ stageId, isPlaytest })
      setLastVictoryMeta(null)
    }
  }, [sessionView, selectedStageId, selectedDifficultyId, playtestMap, syncIngots])

  return { lastVictoryMeta }
}
